/*
 * Copyright (c) 2021. Datafood srl
 * Developed by Revo Digital - revodigital.it
 * ---
 * Author: leonardoviada
 * File: toast.tsx
 * Project: mle-client
 * Committed last: 2021/11/12 @ 825
 * ---
 */

import { Intent, IToaster, IToastProps, Toaster } from '@blueprintjs/core';
import { MeasureUnits, ShapeTypes }               from './enums';
import React                                      from 'react';

/**
 * Gestisce la generazione di istanze per l'invio di notifiche nel client.
 * Si basa sul Toaster di BlueprintJs, vedi https://blueprintjs.com/docs/#core/components/toast.toaster.
 * Opera secondo la logica descritta dal pattern Singleton [GoF]
 */
export class MleToaster {
  /**
   * Istanza corrente del Toaster.
   * @type {IToaster}
   * @private
   */
  private static _instance: IToaster;

  /**
   * Permette al costruttore di eseguito solo da questa classe o suoi eredi
   * @protected
   */
  protected constructor() {}

  /**
   * Secondo il pattern Singleton [GoF], si assicura che nell'ambiente di esecuzione,
   * sia presente una sola istanza della classe.
   * @returns {Toaster}
   */
  static getInstance() {
    if (!MleToaster._instance)
      MleToaster._instance = Toaster.create({
        maxToasts: 2,
        canEscapeKeyClear: true,
        usePortal: true
      });
    return MleToaster._instance;
  }
}

/**
 * Elenca le notifiche disponibili.
 * Alcune di queste sono funzioni che consentono, tramite parametro, di personalizzare il contenuto.
 * @type {{LOGIN_SUCCESSFUL: IToastProps, IMAGE_GENERATED: IToastProps, GENERIC_ERROR: IToastProps, NOT_FOUND: IToastProps, REGISTRATION_SUCCESSFUL: IToastProps, MEASURE_UNIT: (measureUnit: MeasureUnits) => IToastProps, FORBIDDEN: IToastProps, INVALID_NEW_OBJECT: (shapeType: ShapeTypes) => IToastProps, LOGOUT_SUCCESSFUL: IToastProps, AUTH_REQUIRED: IToastProps, INIT_CANVAS: IToastProps, PROJECT_CREATED: IToastProps, PROJECT_REMOVED: IToastProps}}
 */
export const Toasts = {
  LOGIN_SUCCESSFUL: {
    intent: Intent.SUCCESS,
    message: 'Accesso effettuato',
    icon: 'log-in',
  } as IToastProps,
  REGISTRATION_SUCCESSFUL: {
    intent: Intent.SUCCESS,
    message: 'Registrazione effettuata',
    icon: 'tick-circle',
  } as IToastProps,
  LOGOUT_SUCCESSFUL: {
    intent: Intent.PRIMARY,
    message: 'Disconnesso',
    icon: 'log-out',
  } as IToastProps,
  AUTH_REQUIRED: {
    intent: Intent.WARNING,
    message: 'Autenticazione fallita, ripeti l\'accesso',
    action: {
      text: <span className="font-mono">E401</span>
    },
    icon: 'warning-sign',
  } as IToastProps,
  FORBIDDEN: {
    intent: Intent.WARNING,
    message: 'Operazione non consentita per ruolo corrente',
    action: {
      text: <span className="font-mono">E403</span>
    },
    icon: 'lock',
  } as IToastProps,
  UNABLE_SELECT_ACTIVE: {
    intent: Intent.WARNING,
    message: 'Impossibile selezionare una ricetta attiva',
    icon: 'warning-sign'
  } as IToastProps,
  NOT_FOUND: {
    intent: Intent.DANGER,
    message: 'Entità non trovata',
    action: {
      text: <span className="font-mono">E404</span>
    },
    icon: 'error',
  } as IToastProps,
  GENERIC_ERROR: {
    intent: Intent.DANGER,
    message: 'Si è verificato un errore, riprova',
    action: {
      text: <span className="font-mono">E500</span>
    },
    icon: 'error',
  } as IToastProps,
  PROJECT_CREATED: {
    intent: Intent.SUCCESS,
    message: 'Progetto creato',
    icon: 'tick-circle'
  } as IToastProps,
  PROJECT_IMPORTED: {
    intent: Intent.SUCCESS,
    message: 'Progetto importato',
    icon: 'tick-circle'
  } as IToastProps,
  INIT_CANVAS: {
    intent: Intent.SUCCESS,
    message: 'Area di lavoro inizializzata',
    icon: 'tick-circle',
  } as IToastProps,
  PROJECT_REMOVED: {
    intent: Intent.SUCCESS,
    message: 'Progetto eliminato',
    icon: 'tick-circle',
  } as IToastProps,
  IMAGE_GENERATED: {
    intent: Intent.SUCCESS,
    message: 'Immagine generata, procedi con il download',
    icon: 'media',
  } as IToastProps,
  INCOMPLETE_FORM: {
    intent: Intent.WARNING,
    message: 'Compila tutti i campi richiesti per procedere',
    icon: 'form',
  } as IToastProps,
  AE_NOT_LINKED: {
    intent: Intent.DANGER,
    message: 'Nessun utente collegato alle credenziali',
    icon: 'user',
  } as IToastProps,
  INVALID_NEW_OBJECT: (shapeType: ShapeTypes) => ({
    intent: Intent.DANGER,
    message: 'Impossibile aggiungere entità richiesta',
    icon: 'error',
    action: {
      text: <span className="font-mono">{ shapeType }</span>
    },
  } as IToastProps),
  MEASURE_UNIT: (measureUnit: MeasureUnits) => ({
    intent: Intent.NONE,
    message: <div>Progetto in <strong>{ measureUnit }</strong></div>,
    icon: 'info-sign',
  } as IToastProps),
  SAVE_ON_EXIT_SUCCESS: (title: string) => ({
    icon: 'tick-circle',
    message: (
      <div>Progetto <strong>{ title }</strong> salvato
      </div>),
    intent: 'success'
  } as IToastProps),
  ML_LINK_SUCCESSFUL: {
    icon: 'tick-circle',
    message: 'Accesso a istanza MyLabel effettuato',
    intent: 'success'
  } as IToastProps,
  ML_LINK_FAILED: {
    message: 'Impossibile collegare MyLabel',
    intent: Intent.DANGER,
    icon: 'data-connection'
  } as IToastProps,
  ML_RECIPE_SELECTED: {
    message: 'Ricetta MyLabel collegata con successo',
    intent: Intent.SUCCESS,
    icon: 'data-connection'
  } as IToastProps,
  UNABLE_TO_CONNECT_RC: {
    message: 'Impossibile collegare ricetta attiva',
    intent: Intent.DANGER,
    icon: 'error'
  } as IToastProps,
  DATA_ORIGIN_REMOVED: {
    message: 'Origine dati rimossa',
    intent: Intent.SUCCESS,
    icon: 'tick-circle'
  } as IToastProps,
  DYNAMIC_FIELD_INSERTED: {
    message: 'Campo dinamico inserito',
    intent: Intent.NONE,
    icon: 'lightning',
    timeout: 2
  } as IToastProps,
  IMAGE_EXPORT_ERROR: {
    message: 'Impossibile esportare l\'immagine',
    intent: Intent.DANGER,
    icon: 'export',
  } as IToastProps,
  PROJECT_ERROR: {
    message: 'Impossibile creare il progetto',
    intent: Intent.DANGER,
    icon: 'error'
  } as IToastProps,
  IMPORT_ERROR: {
    message: 'Impossibile importate il progetto',
    intent: Intent.DANGER,
    icon: 'error'
  } as IToastProps
};
